
import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import {
  BarChart3,
  Briefcase,
  Building2,
  ChevronLeft,
  ChevronRight,
  FileText,
  FolderKanban,
  Home,
  Library,
  MessageSquare,
  Settings,
  Sparkles,
  Users,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarRail,
  SidebarSeparator,
  useSidebar
} from "@/components/ui/sidebar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAuth } from "@/contexts/auth/AuthContext";
import { Button } from "@/components/ui/button";

const mainNavItems = [
  {
    title: "Dashboard",
    href: "/",
    icon: Home,
  },
  {
    title: "Departments",
    href: "/departments",
    icon: Building2,
  },
  {
    title: "Team",
    href: "/team",
    icon: Users,
  },
  {
    title: "Analytics",
    href: "/analytics",
    icon: BarChart3,
  },
  {
    title: "Resources",
    href: "/resources",
    icon: Library,
  },
  {
    title: "Dream",
    href: "/dream",
    icon: Sparkles,
  }
];

const departmentNavItems = [
  {
    title: "Operations",
    href: "/departments/operations",
    icon: Briefcase,
  },
  {
    title: "Marketing",
    href: "/departments/marketing",
    icon: BarChart3,
  },
  {
    title: "Engineering",
    href: "/departments/engineering",
    icon: FolderKanban,
  },
  {
    title: "Sales",
    href: "/departments/sales",
    icon: BarChart3,
  },
  {
    title: "Finance",
    href: "/departments/finance",
    icon: BarChart3,
  },
  {
    title: "Legal",
    href: "/departments/legal",
    icon: FileText,
  },
  {
    title: "Support",
    href: "/departments/support",
    icon: MessageSquare,
  }
];

const CustomSidebarTrigger = () => {
  const { toggleSidebar, state } = useSidebar();
  
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={toggleSidebar}
      className="absolute right-0 top-4 z-30 translate-x-1/2 bg-background border shadow-sm h-8 w-8 rounded-full flex items-center justify-center"
    >
      {state === "expanded" ? (
        <ChevronLeft className="h-4 w-4" />
      ) : (
        <ChevronRight className="h-4 w-4" />
      )}
    </Button>
  );
};

const AppSidebar = () => {
  const { pathname } = useLocation();
  const { user, currentOrganization } = useAuth();

  if (!user || !currentOrganization) {
    return null;
  }

  const userRole = currentOrganization.role;
  const isPrimaryFounder = userRole === 'primary_founder';
  const isCoFounder = userRole === 'co_founder';

  return (
    <Sidebar>
      <SidebarHeader className="flex items-center justify-center h-16 px-4 border-b relative">
        <strong className="text-lg">Founders CRM</strong>
        <CustomSidebarTrigger />
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Main</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainNavItems.map((item) => (
                <SidebarMenuItem key={item.href}>
                  <SidebarMenuButton 
                    asChild 
                    isActive={pathname === item.href}
                    tooltip={item.title}
                  >
                    <Link to={item.href}>
                      <item.icon className="mr-2 h-4 w-4" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        
        <SidebarSeparator />
        
        <SidebarGroup>
          <SidebarGroupLabel>Departments</SidebarGroupLabel>
          <SidebarGroupContent>
            <ScrollArea className="h-[300px] pr-2">
              <SidebarMenu>
                {departmentNavItems.map((item) => (
                  <SidebarMenuItem key={item.href}>
                    <SidebarMenuButton 
                      asChild 
                      isActive={pathname === item.href}
                      tooltip={item.title}
                    >
                      <Link to={item.href}>
                        <item.icon className="mr-2 h-4 w-4" />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </ScrollArea>
          </SidebarGroupContent>
        </SidebarGroup>
        
        {(isPrimaryFounder || isCoFounder) && (
          <>
            <SidebarSeparator />
            <SidebarGroup>
              <SidebarGroupLabel>Administration</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      isActive={pathname === '/settings'}
                      tooltip="Settings"
                    >
                      <Link to="/settings">
                        <Settings className="mr-2 h-4 w-4" />
                        <span>Settings</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}
      </SidebarContent>
      <SidebarRail />
    </Sidebar>
  );
};

export default AppSidebar;
